/**
 * ══════════════════════════════════════════════════════════════════
 * DASHBOARD.JSX v34.0 - PLANIVIO REDESIGN
 * ══════════════════════════════════════════════════════════════════
 *
 * 🎨 v34.0: KOMPLETT NEUES LAYOUT basierend auf planivio_dashboard_redesign.html
 *           - Greeting Section (Willkommen + Datum)
 *           - Quick Stats 2×2 Grid (Shopping/Tasks/Calendar/Budget)
 *           - Weekly Timeline Strip (7 Tage mit Dots)
 *           - Today's Schedule (Tages-Termine als Cards)
 *           - Shopping Lists (Progress Bars)
 *           - Open Tasks (Checkbox Items mit Priority)
 *           - Budget Mini Widget (Einnahmen/Ausgaben/Bilanz + Bar)
 *           - Alle Styles via Dashboard.css v3.0 (kein inline styles)
 *
 * 🔧 v33.3: RECURRING APPOINTMENTS DISPLAY
 * ✅ v33.0: TOKEN CONTEXT - Verwendet zentrales Token-Management
 * ✅ v32.0: DEPENDENCY OPTIMIERUNG
 * ✅ v31.0: ANALYTICS TRACKING
 * ✅ v30.0: Auth Error Handling
 * ✅ v29.0: Planivio Rebranding
 *
 * @version 34.0.0 - Planivio Redesign
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToken } from '../../context/TokenContext';
import { useTranslation } from '../../context/I18nContext';
import { useTheme } from '../../context/ThemeContext';
import Loading, { Skeleton } from '../common/Loading';
import './Dashboard.css';

// ══════════════════════════════════════════════════════════════════
// ANALYTICS + API CONFIG
// ══════════════════════════════════════════════════════════════════
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const LOADING_DELAY_MS = 200;
const MAX_RETRIES = 3;
const RETRY_DELAY_BASE_MS = 1000;

console.log('🏠 Dashboard v34.0 loaded - PLANIVIO REDESIGN');

const trackAnalyticsEvent = async (token, eventName, metadata = {}) => {
  if (!token || !eventName) return;
  try {
    const event = {
      type: 'event',
      event_name: String(eventName).substring(0, 100),
      component: String(metadata.component || 'Dashboard').substring(0, 100),
      metadata: metadata,
      timestamp: new Date().toISOString()
    };
    const response = await fetch(`${API_BASE_URL}/api/analytics/track/batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ events: [event] })
    });
    if (response.ok) console.log(`✅ Analytics tracked: ${eventName}`);
  } catch (err) {
    console.warn(`❌ Analytics error: ${eventName}`, err.message);
  }
};

// ══════════════════════════════════════════════════════════════════
// GLOBAL CACHE
// ══════════════════════════════════════════════════════════════════
let GLOBAL_DASHBOARD_CACHE = {
  shopping: [], appointments: [], notes: [], tasks: [], recipes: [], budget: null, householdMembers: []
};
let GLOBAL_HAS_LOADED = false;
let GLOBAL_LOADING_PROMISE = null;

// ══════════════════════════════════════════════════════════════════
// WEEKDAY HELPERS
// ══════════════════════════════════════════════════════════════════
const WEEKDAY_NAMES = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

function getWeekDays() {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((dayOfWeek + 6) % 7));

  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    days.push({
      name: WEEKDAY_NAMES[d.getDay()],
      num: d.getDate(),
      date: d.toISOString().split('T')[0],
      isToday: d.toDateString() === today.toDateString()
    });
  }
  return days;
}

function getAppointmentCategory(apt) {
  const title = (apt.title || '').toLowerCase();
  const cat = (apt.category || '').toLowerCase();
  if (cat === 'meeting' || title.includes('standup') || title.includes('meeting')) return 'meeting';
  if (cat === 'family' || title.includes('kind') || title.includes('abhol')) return 'family';
  if (cat === 'shopping' || title.includes('einkauf') || title.includes('rewe') || title.includes('aldi')) return 'shopping';
  if (cat === 'health' || title.includes('arzt') || title.includes('zahnarzt')) return 'health';
  if (cat === 'recipe' || title.includes('kochen') || title.includes('rezept')) return 'recipe';
  if (cat === 'work' || title.includes('arbeit') || title.includes('büro')) return 'work';
  return 'default';
}

// ══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════
const Dashboard = ({ active, onNavigate, householdName = 'Mein Haushalt' }) => {
  const { isAuthenticated, isLoading: isAuthLoading, signOut } = useAuth();
  const { token, loading: tokenLoading, refreshToken } = useToken();
  const { t } = useTranslation();
  const { isDark } = useTheme();

  const [dashboardData, setDashboardData] = useState(() => ({
    ...GLOBAL_DASHBOARD_CACHE, loading: !GLOBAL_HAS_LOADED
  }));
  const [isLoading, setIsLoading] = useState(!GLOBAL_HAS_LOADED);
  const [showLoading, setShowLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(!GLOBAL_HAS_LOADED);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedDay, setSelectedDay] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadingTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  const signOutRef = useRef(signOut);
  const hasTrackedViewRef = useRef(false);

  useEffect(() => { signOutRef.current = signOut; }, [signOut]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (token && !hasTrackedViewRef.current && active) {
      hasTrackedViewRef.current = true;
      trackAnalyticsEvent(token, 'component_view', { component: 'Dashboard', isMobile: window.innerWidth <= 768 });
    }
  }, [token, active]);

  const trackEvent = useCallback(async (eventName, metadata = {}) => {
    if (!token) return;
    trackAnalyticsEvent(token, eventName, { component: 'Dashboard', ...metadata });
  }, [token]);

  useEffect(() => {
    if (dashboardData.shopping.length > 0 || dashboardData.tasks.length > 0) {
      GLOBAL_DASHBOARD_CACHE = {
        shopping: dashboardData.shopping, appointments: dashboardData.appointments,
        notes: dashboardData.notes, tasks: dashboardData.tasks,
        recipes: dashboardData.recipes, budget: dashboardData.budget
      };
    }
  }, [dashboardData]);

  const handleNavigate = useCallback((module, source = 'unknown') => {
    trackEvent('dashboard_navigate', { target_module: module, source });
    if (onNavigate) onNavigate(module);
  }, [onNavigate, trackEvent]);

  const isAuthError = useCallback((error) => {
    if (!error) return false;
    const s = String(error.message || error).toLowerCase();
    return ['invalid_grant','invalid_token','token expired','unauthorized','session expired','login required']
      .some(p => s.includes(p));
  }, []);

  const handleForceLogout = useCallback(async (reason) => {
    trackEvent('dashboard_force_logout', { reason });
    GLOBAL_HAS_LOADED = false;
    GLOBAL_LOADING_PROMISE = null;
    GLOBAL_DASHBOARD_CACHE = { shopping: [], appointments: [], notes: [], tasks: [], recipes: [], budget: null, householdMembers: [] };
    try { localStorage.clear(); sessionStorage.clear(); } catch (e) {}
    if (signOutRef.current) {
      try { await signOutRef.current(window.location.origin); } catch (e) { window.location.href = window.location.origin; }
    } else { window.location.href = window.location.origin; }
  }, [trackEvent]);

  const fetchWithToken = useCallback(async (url, authToken, signal) => {
    try {
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' }, signal
      });
      if (response.ok) return await response.json();
      if (response.status === 401 || response.status === 403) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`auth_error: ${errorData.error || 'unauthorized'}`);
      }
      return null;
    } catch (err) {
      if (err.name !== 'AbortError') throw err;
      return null;
    }
  }, []);

  const fetchDashboardFromBackend = useCallback(async (signal) => {
    if (!token) throw new Error(t('dashboard.noToken'));
    const weekStart = (() => { const d = new Date(); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return d.toISOString().split('T')[0]; })();
    const [shoppingResult, calendarResult, notesResult, tasksResult, recipesResult, budgetResult, householdResult] =
      await Promise.all([
        fetchWithToken(`${API_BASE_URL}/api/shopping`, token, signal),
        fetchWithToken(`${API_BASE_URL}/api/calendar?start_date=${weekStart}`, token, signal),
        fetchWithToken(`${API_BASE_URL}/api/notes`, token, signal),
        fetchWithToken(`${API_BASE_URL}/api/tasks`, token, signal),
        fetchWithToken(`${API_BASE_URL}/api/recipes`, token, signal),
        fetchWithToken(`${API_BASE_URL}/api/budget/summary`, token, signal),
        fetchWithToken(`${API_BASE_URL}/api/household/members`, token, signal)
      ]);
    const householdMembers = Array.isArray(householdResult) ? householdResult : householdResult?.members || [];
    return {
      shopping: Array.isArray(shoppingResult) ? shoppingResult : shoppingResult?.lists || [],
      appointments: Array.isArray(calendarResult) ? calendarResult : calendarResult?.appointments || [],
      notes: Array.isArray(notesResult) ? notesResult : notesResult?.notes || [],
      tasks: Array.isArray(tasksResult) ? tasksResult : tasksResult?.tasks || [],
      recipes: Array.isArray(recipesResult) ? recipesResult : recipesResult?.recipes || [],
      budget: budgetResult || null,
      householdMembers
    };
  }, [fetchWithToken, t, token]);

  const loadDashboardData = useCallback(async (forceRefresh = false, signal) => {
    if (!token) return;
    if (GLOBAL_HAS_LOADED && !forceRefresh && (GLOBAL_DASHBOARD_CACHE.shopping.length > 0 || GLOBAL_DASHBOARD_CACHE.tasks.length > 0)) {
      if (isMountedRef.current) {
        setDashboardData({ ...GLOBAL_DASHBOARD_CACHE, loading: false });
        setIsLoading(false); setIsInitialLoad(false);
      }
      return;
    }
    if (GLOBAL_LOADING_PROMISE) {
      try {
        const result = await GLOBAL_LOADING_PROMISE;
        if (isMountedRef.current && !signal?.aborted) {
          setDashboardData({ ...result, loading: false });
          setIsLoading(false); setIsInitialLoad(false); setRetryCount(0);
        }
      } catch (err) {
        if (isAuthError(err)) { handleForceLogout(err.message); return; }
        if (isMountedRef.current && !signal?.aborted) { setError(err.message); setIsLoading(false); setIsInitialLoad(false); }
      }
      return;
    }
    if (isMountedRef.current) {
      setIsLoading(true); setError(null);
      if (!isInitialLoad) {
        loadingTimerRef.current = setTimeout(() => { if (isMountedRef.current) setShowLoading(true); }, LOADING_DELAY_MS);
      }
    }
    GLOBAL_LOADING_PROMISE = fetchDashboardFromBackend(signal);
    try {
      const result = await GLOBAL_LOADING_PROMISE;
      if (signal?.aborted) return;
      GLOBAL_DASHBOARD_CACHE = result;
      GLOBAL_HAS_LOADED = true;
      if (isMountedRef.current) { setDashboardData({ ...result, loading: false }); setIsInitialLoad(false); setRetryCount(0); }
    } catch (err) {
      trackEvent('dashboard_load_error', { error: err.message });
      if (isAuthError(err)) {
        if (retryCount >= MAX_RETRIES) { handleForceLogout(err.message); return; }
        try { await refreshToken(); } catch (e) {}
        const delay = RETRY_DELAY_BASE_MS * Math.pow(2, retryCount);
        if (isMountedRef.current) {
          setRetryCount(prev => prev + 1);
          setError(`Authentifizierungsfehler - Wiederhole in ${delay/1000}s... (${retryCount + 1}/${MAX_RETRIES})`);
        }
        setTimeout(() => { if (isMountedRef.current) { GLOBAL_LOADING_PROMISE = null; loadDashboardData(true, signal); } }, delay);
        return;
      }
      if (isMountedRef.current && !signal?.aborted) {
        setError(err.message); setIsInitialLoad(false);
        if (GLOBAL_DASHBOARD_CACHE.shopping.length > 0 || GLOBAL_DASHBOARD_CACHE.tasks.length > 0) {
          setDashboardData({ ...GLOBAL_DASHBOARD_CACHE, loading: false });
        }
      }
    } finally {
      GLOBAL_LOADING_PROMISE = null;
      if (isMountedRef.current) { setIsLoading(false); setShowLoading(false); }
      if (loadingTimerRef.current) { clearTimeout(loadingTimerRef.current); loadingTimerRef.current = null; }
    }
  }, [isInitialLoad, fetchDashboardFromBackend, retryCount, isAuthError, handleForceLogout, trackEvent, token, refreshToken]);

  useEffect(() => {
    if (!active) return;
    const abortController = new AbortController();
    if (GLOBAL_HAS_LOADED && (GLOBAL_DASHBOARD_CACHE.shopping.length > 0 || GLOBAL_DASHBOARD_CACHE.tasks.length > 0)) {
      setDashboardData({ ...GLOBAL_DASHBOARD_CACHE, loading: false });
      setIsLoading(false); setIsInitialLoad(false);
      return;
    }
    if (isAuthLoading || tokenLoading) return;
    if (!isAuthenticated) { setIsLoading(false); setIsInitialLoad(false); return; }
    if (!token) return;
    const timeoutId = setTimeout(() => { loadDashboardData(false, abortController.signal); }, 100);
    return () => { clearTimeout(timeoutId); abortController.abort(); };
  }, [active, isAuthenticated, isAuthLoading, tokenLoading, token, loadDashboardData]);

  const handleRetry = useCallback(() => {
    trackEvent('dashboard_retry', { type: 'manual' });
    GLOBAL_HAS_LOADED = false; GLOBAL_LOADING_PROMISE = null;
    setError(null); setRetryCount(0); setIsInitialLoad(true);
    loadDashboardData(true);
  }, [loadDashboardData, trackEvent]);

  // ══════════════════════════════════════════════════════════════════
  // BERECHNUNGEN
  // ══════════════════════════════════════════════════════════════════
  const getOpenTasks = () => dashboardData.tasks.filter(t => !t.completed && !t.done).length;
  const getUpcomingAppointments = () => {
    const today = new Date().toISOString().split('T')[0];
    return dashboardData.appointments.filter(a => (a.date?.split('T')[0] || a.date) === today).length;
  };
  const getUncheckedItems = () => dashboardData.shopping.reduce((sum, list) => sum + (list.items?.filter(i => !i.checked)?.length || 0), 0);

  const formatTime = (timeString) => {
    if (!timeString || timeString === '00:00:00' || timeString === '00:00') return null;
    return timeString.substring(0, 5);
  };

  const formatAppointmentDate = (dateString) => {
    if (!dateString) return '';
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const aptDate = new Date(dateString); aptDate.setHours(0, 0, 0, 0);
    const diffDays = Math.round((aptDate - today) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return t('dashboard.today');
    if (diffDays === 1) return t('dashboard.tomorrow') || 'Morgen';
    if (diffDays > 1 && diffDays <= 7) return `in ${diffDays} Tagen`;
    return aptDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };

  const getPersonName = (personValue) => {
    if (!personValue || personValue === 'alle') return null;
    const numericId = parseInt(personValue, 10);
    if (!isNaN(numericId) && dashboardData.householdMembers?.length > 0) {
      const member = dashboardData.householdMembers.find(m => m.id === numericId);
      if (member) return member.name;
    }
    return personValue;
  };

  const getBudgetIncome = () => dashboardData.budget?.summary?.income || 0;
  const getBudgetExpense = () => dashboardData.budget?.summary?.expense || 0;
  const getBudgetBalance = () => dashboardData.budget?.summary?.balance || 0;
  const hasBudgetData = () => getBudgetIncome() > 0 || getBudgetExpense() > 0;
  const formatEuro = (amount) => new Intl.NumberFormat('de-DE', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(amount);

  const getTodayFormatted = () => new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const getWeekDaysWithDots = () => {
    const weekDays = getWeekDays();
    return weekDays.map(day => {
      const dayApts = dashboardData.appointments.filter(a => (a.date?.split('T')[0] || a.date) === day.date);
      const dots = dayApts.slice(0, 3).map(a => {
        const cat = getAppointmentCategory(a);
        const colorMap = { meeting: '#3B82F6', family: '#F59E0B', shopping: '#22C55E', health: '#EC4899', recipe: '#EF4444', work: '#3B82F6', default: '#4A90A4' };
        return colorMap[cat] || '#4A90A4';
      });
      return { ...day, dots };
    });
  };

  const getSelectedDayAppointments = () => {
    return dashboardData.appointments
      .filter(a => (a.date?.split('T')[0] || a.date) === selectedDay)
      .sort((a, b) => (a.time || a.start_time || '').localeCompare(b.time || b.start_time || ''));
  };

  const getSelectedDayLabel = () => {
    const today = new Date().toISOString().split('T')[0];
    if (selectedDay === today) return 'Heute';
    const d = new Date(selectedDay + 'T00:00:00');
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
    if (selectedDay === tomorrow.toISOString().split('T')[0]) return 'Morgen';
    return d.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const getBudgetPercentage = () => {
    const income = getBudgetIncome();
    if (income === 0) return 0;
    return Math.min(100, Math.round((getBudgetExpense() / income) * 100));
  };

  // ══════════════════════════════════════════════════════════════════
  // RENDER: SKELETON
  // ══════════════════════════════════════════════════════════════════
  const hasData = dashboardData.shopping.length > 0 || dashboardData.tasks.length > 0;

  if ((isInitialLoad || tokenLoading) && !hasData && !error) {
    return (
      <div className={`dashboard-redesign ${isDark ? 'dark' : ''}`}>
        <div className="db-greeting">
          <div className="db-skeleton" style={{ width: 120, height: 14, marginBottom: 8 }} />
          <div className="db-skeleton" style={{ width: 200, height: 28, marginBottom: 6 }} />
          <div className="db-skeleton" style={{ width: 160, height: 12 }} />
        </div>
        <div className="db-stats-grid">
          {[1,2,3,4].map(i => <div key={i} className="db-skeleton" style={{ height: 76, borderRadius: 16 }} />)}
        </div>
        <div className="db-skeleton" style={{ height: 60, borderRadius: 10, marginBottom: 18 }} />
        {[1,2,3].map(i => <div key={i} className="db-skeleton" style={{ height: 64, borderRadius: 16, marginBottom: 10 }} />)}
      </div>
    );
  }

  if (isAuthLoading) {
    return (
      <div className={`dashboard-redesign ${isDark ? 'dark' : ''}`}>
        <div className="db-loading"><div className="db-spinner" /><p>🔐 Authentifizierung...</p></div>
      </div>
    );
  }

  if (error && !hasData) {
    const isAuthRetrying = error.includes('Authentifizierungsfehler') && retryCount < MAX_RETRIES;
    return (
      <div className={`dashboard-redesign ${isDark ? 'dark' : ''}`}>
        <div className="db-error-container">
          <span className="db-error-icon">{isAuthRetrying ? '🔄' : '⚠️'}</span>
          <p className="db-error-text">{error}</p>
          {!isAuthRetrying && (
            <>
              <button className="db-btn-retry" onClick={handleRetry}>🔄 {t('dashboard.retry')}</button>
              <button className="db-btn-logout" onClick={() => handleForceLogout('Manual')}>🔒 Neu einloggen</button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════
  // RENDER: MAIN – PLANIVIO REDESIGN
  // ══════════════════════════════════════════════════════════════════
  const weekDays = getWeekDaysWithDots();
  const todayAppointments = getSelectedDayAppointments();
  const openTasks = dashboardData.tasks.filter(t => !t.completed && !t.done).slice(0, 4);
  const completedTasks = dashboardData.tasks.filter(t => t.completed || t.done).slice(0, 1);
  const budgetPct = getBudgetPercentage();

  return (
    <div className={`dashboard-redesign ${isDark ? 'dark' : ''}`}>

      {showLoading && !retryCount && (
        <div className="db-loading-indicator">
          <span style={{ animation: 'db-pulse 1s infinite' }}>⏳</span>
          <span>Aktualisiere...</span>
        </div>
      )}

      {retryCount > 0 && retryCount < MAX_RETRIES && (
        <div className="db-auth-indicator">
          <span style={{ animation: 'db-pulse 1s infinite' }}>🔄</span>
          <span>Authentifizierung... ({retryCount}/{MAX_RETRIES})</span>
        </div>
      )}

      {error && hasData && (
        <div className="db-error-banner">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* ── GREETING ── */}
      <div className="db-greeting db-animate-in">
        <div className="db-greeting-hello">{t('dashboard.welcome')} 👋</div>
        <div className="db-greeting-name">{householdName}</div>
        <div className="db-greeting-date">{getTodayFormatted()}</div>
      </div>

      {/* ── QUICK STATS 2×2 (mobile) / 4×1 (desktop) ── */}
      <div className="db-stats-grid db-animate-in db-delay-1">
        <div className="db-stat-card shopping" onClick={() => handleNavigate('shopping', 'stat_card')}>
          <div className="db-stat-icon shopping">🛒</div>
          <div className="db-stat-info">
            <div className="db-stat-value shopping">{dashboardData.shopping.length}</div>
            <div className="db-stat-label">Listen · {getUncheckedItems()} offen</div>
          </div>
        </div>
        <div className="db-stat-card tasks" onClick={() => handleNavigate('tasks', 'stat_card')}>
          <div className="db-stat-icon tasks">☑️</div>
          <div className="db-stat-info">
            <div className="db-stat-value tasks">{getOpenTasks()}</div>
            <div className="db-stat-label">Aufgaben offen</div>
          </div>
        </div>
        <div className="db-stat-card calendar" onClick={() => handleNavigate('calendar', 'stat_card')}>
          <div className="db-stat-icon calendar">📅</div>
          <div className="db-stat-info">
            <div className="db-stat-value calendar">{getUpcomingAppointments()}</div>
            <div className="db-stat-label">Termine heute</div>
          </div>
        </div>
        <div className="db-stat-card budget" onClick={() => handleNavigate('budget', 'stat_card')}>
          <div className="db-stat-icon budget">💰</div>
          <div className="db-stat-info">
            <div className={`db-stat-value ${getBudgetBalance() >= 0 ? 'budget-pos' : 'budget-neg'}`}>
              {getBudgetBalance() >= 0 ? '+' : ''}{formatEuro(getBudgetBalance())}
            </div>
            <div className="db-stat-label">{t('dashboard.balance')}</div>
          </div>
        </div>
      </div>

      {/* ── WEEKLY TIMELINE ── */}
      <div className="db-animate-in db-delay-3">
        <div className="db-section-header">
          <div className="db-section-title">Diese Woche</div>
          <button className="db-section-link" onClick={() => handleNavigate('calendar', 'week_strip')}>
            Alle Termine →
          </button>
        </div>
        <div className="db-week-strip">
          {weekDays.map((day, idx) => (
            <div key={idx} className={`db-week-day ${day.isToday ? 'today' : ''} ${day.date === selectedDay ? 'selected' : ''}`} onClick={() => setSelectedDay(day.date)}>
              <div className="db-week-day-name">{day.name}</div>
              <div className="db-week-day-num">{day.num}</div>
              <div className="db-week-day-dots">
                {day.dots.map((color, di) => (
                  <div key={di} className="db-week-dot" style={{ background: color }} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── TODAY'S SCHEDULE ── */}
      <div className="db-animate-in db-delay-4">
        <div className="db-section-header">
          <div className="db-section-title">{getSelectedDayLabel()}</div>
          {todayAppointments.length > 0 && (
            <span className="db-section-count">{todayAppointments.length} Termine</span>
          )}
        </div>
        {todayAppointments.length > 0 ? (
          todayAppointments.slice(0, 4).map((apt, idx) => {
            const timeDisplay = formatTime(apt.time) || formatTime(apt.start_time);
            const cat = getAppointmentCategory(apt);
            const personName = getPersonName(apt.person);
            const recurringIcon = (apt.recurring && apt.recurring !== 'none') ? '🔄 ' : '';
            return (
              <div key={apt.id || idx} className={`db-today-card cat-${cat}`}>
                <div className="db-today-time">{timeDisplay || '⏰'}</div>
                <div className="db-today-content">
                  <div className="db-today-title">{recurringIcon}{apt.title || t('dashboard.appointment')}</div>
                  <div className="db-today-meta">
                    {!timeDisplay && (t('dashboard.allDay') || 'Ganztägig')}
                    {personName && ` · 👤 ${personName}`}
                    {apt.location && ` · 📍 ${apt.location}`}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="db-empty">
            <div className="db-empty-icon">📅</div>
            <p className="db-empty-text">Keine Termine heute</p>
          </div>
        )}
      </div>

      {/* ── SHOPPING LISTS ── */}
      <div className="db-animate-in db-delay-5" style={{ marginTop: 18 }}>
        <div className="db-section-header">
          <div className="db-section-title">Einkaufslisten</div>
          <button className="db-section-link" onClick={() => handleNavigate('shopping', 'shopping_section')}>Alle Listen →</button>
        </div>
        {dashboardData.shopping.length > 0 ? (
          dashboardData.shopping.slice(0, 3).map((list, idx) => {
            const checkedItems = list.items?.filter(i => i.checked)?.length || 0;
            const totalItems = list.items?.length || 0;
            const progress = totalItems > 0 ? (checkedItems / totalItems) * 100 : 0;
            return (
              <div key={list.id || idx} className="db-list-card" onClick={() => handleNavigate('shopping', 'list_card')}>
                <div className="db-list-header">
                  <div className="db-list-name">📋 {list.name || t('dashboard.shoppingList')}</div>
                  <div className="db-list-count">{checkedItems} / {totalItems}</div>
                </div>
                <div className="db-list-progress-bg">
                  <div className="db-list-progress-fill" style={{ width: `${progress}%`, background: progress === 100 ? 'var(--db-green)' : 'var(--db-accent)' }} />
                </div>
              </div>
            );
          })
        ) : (
          <div className="db-empty"><div className="db-empty-icon">🛒</div><p className="db-empty-text">{t('dashboard.noLists')}</p></div>
        )}
      </div>

      {/* ── OPEN TASKS ── */}
      <div className="db-animate-in db-delay-6" style={{ marginTop: 18 }}>
        <div className="db-section-header">
          <div className="db-section-title">Offene Aufgaben</div>
          <button className="db-section-link" onClick={() => handleNavigate('tasks', 'tasks_section')}>Alle →</button>
        </div>
        {openTasks.length > 0 ? (
          <>
            {openTasks.map((task, idx) => {
              const priorityColors = { high: 'var(--db-red)', medium: 'var(--db-yellow)', low: 'var(--db-blue)' };
              const personName = getPersonName(task.assigned_to || task.person);
              const dueText = task.due_date ? formatAppointmentDate(task.due_date) : '';
              return (
                <div key={task.id || idx} className="db-task-item" onClick={() => handleNavigate('tasks', 'task_item')}>
                  <div className="db-task-check" />
                  <div className="db-task-text">
                    <div className="db-task-title">{task.title || t('dashboard.task')}</div>
                    <div className="db-task-due">{dueText}{personName ? ` · ${personName}` : ''}</div>
                  </div>
                  <div className="db-task-priority" style={{ background: priorityColors[task.priority] || 'var(--db-text-light)' }} />
                </div>
              );
            })}
            {completedTasks.map((task, idx) => (
              <div key={`done-${idx}`} className="db-task-item completed">
                <div className="db-task-check done" />
                <div className="db-task-text">
                  <div className="db-task-title done">{task.title || t('dashboard.task')}</div>
                  <div className="db-task-due">Erledigt{getPersonName(task.assigned_to || task.person) ? ` · ${getPersonName(task.assigned_to || task.person)}` : ''}</div>
                </div>
                <div className="db-task-priority" style={{ background: 'var(--db-text-light)' }} />
              </div>
            ))}
          </>
        ) : (
          <div className="db-empty"><div className="db-empty-icon">🎉</div><p className="db-empty-text">{t('dashboard.noOpenTasks')}</p></div>
        )}
      </div>

      {/* ── BUDGET MINI ── */}
      <div className="db-animate-in db-delay-7" style={{ marginTop: 18 }}>
        <div className="db-section-header">
          <div className="db-section-title">Budget {new Date().toLocaleDateString('de-DE', { month: 'long' })}</div>
          <button className="db-section-link" onClick={() => handleNavigate('budget', 'budget_section')}>Details →</button>
        </div>
        {hasBudgetData() ? (
          <div className="db-budget-widget">
            <div className="db-budget-row">
              <div className="db-budget-item">
                <div className="db-budget-amount" style={{ color: 'var(--db-green)' }}>+{formatEuro(getBudgetIncome())}</div>
                <div className="db-budget-label">{t('budget.income') || 'Einnahmen'}</div>
              </div>
              <div className="db-budget-divider" />
              <div className="db-budget-item">
                <div className="db-budget-amount" style={{ color: 'var(--db-red)' }}>-{formatEuro(getBudgetExpense())}</div>
                <div className="db-budget-label">{t('budget.expenses') || 'Ausgaben'}</div>
              </div>
              <div className="db-budget-divider" />
              <div className="db-budget-item">
                <div className="db-budget-amount" style={{ color: 'var(--db-accent)' }}>
                  {getBudgetBalance() >= 0 ? '+' : ''}{formatEuro(getBudgetBalance())}
                </div>
                <div className="db-budget-label">{t('budget.balance') || 'Bilanz'}</div>
              </div>
            </div>
            <div className="db-budget-bar-bg">
              <div className="db-budget-bar-fill" style={{ width: `${budgetPct}%` }} />
            </div>
            <div className="db-budget-bar-label">
              <span>{budgetPct}% ausgegeben</span>
              <span>{formatEuro(Math.abs(getBudgetBalance()))} übrig</span>
            </div>
          </div>
        ) : (
          <div className="db-empty"><div className="db-empty-icon">💰</div><p className="db-empty-text">{t('dashboard.noBudget')}</p></div>
        )}
      </div>

    </div>
  );
};

export default Dashboard;